// src/Hooks/useApi.js
import { useCallback, useMemo } from "react";
import axios from "axios";
import qs from "qs"; // For query string serialization
import { invalidateFor, prefetchSetup } from "../store/rtkqBridge";

// Cache durations
const TEN_MINUTES = 60 * 10 * 1000;
const CACHE_EXPIRY = TEN_MINUTES;
const MIN_CACHE_EXPIRY = 1000; // 15 seconds

// NEW: Negative-cache TTL for 404 responses (only one fetch per period)
const NOT_FOUND_TTL = 5 * 60 * 1000; // 5 minutes

// Singleton in-memory cache
// Entry shape: { data, timestamp, ttl? }
const cache = new Map();

// Singleton pending requests map to handle deduplication
const pendingRequests = new Map();

// Request queue for throttling
const requestQueue = [];
let isProcessing = false;

// Retry helper for 429 responses (exponential back-off)
const MAX_429_RETRIES = 3;
const retryOn429 = async (fn) => {
  for (let attempt = 0; attempt <= MAX_429_RETRIES; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (error?.response?.status === 429 && attempt < MAX_429_RETRIES) {
        const retryAfter = error.response.headers?.["retry-after"];
        const delayMs = retryAfter
          ? parseInt(retryAfter, 10) * 1000
          : Math.min(1000 * 2 ** attempt, 10000);
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }
      throw error;
    }
  }
};

// ----- NEW: single-flight refresh promise (to dedupe concurrent refreshes)
let refreshPromise = null;

// Utility function to serialize query parameters
const serializeParams = (params) =>
  qs.stringify(params, { arrayFormat: "brackets" });

// Function to generate a unique cache key based on endpoint and params
const generateCacheKey = (endpoint, config) => {
  const { params } = config;
  const serializedParams = params ? `?${serializeParams(params)}` : "";
  return `cache_${endpoint}${serializedParams}`;
};

// Guess a safe fallback body for 404s based on the endpoint
const guessFallbackFor404 = (endpoint) => {
  const e = String(endpoint).toLowerCase();
  if (e.includes("/api/slots")) return [];
  if (e.includes("/slots/")) return [];
  if (e.includes("reservations")) return [];
  if (e.includes("/api/auth-restaurant")) return {}; // setup payload shape handled in RTKQ; here just avoid crashes
  return {};
};

// Function to process the request queue with throttling
const processQueue = async (log) => {
  if (isProcessing) {
    return;
  }
  isProcessing = true;

  while (requestQueue.length > 0) {
    const { requestFn, resolve, reject, cacheKey } = requestQueue.shift();
    try {
      const result = await requestFn();

      // NEW: Support special return to inject custom TTL into cache
      if (result && cacheKey) {
        if (result && result.__cacheWithTtl) {
          const { data, ttl } = result.__cacheWithTtl;
          cache.set(cacheKey, { data, timestamp: Date.now(), ttl });
          log("Cached response (custom TTL) for:", cacheKey, "ttl:", ttl);
          resolve(data);
        } else {
          cache.set(cacheKey, { data: result, timestamp: Date.now() });
          log("Cached response for:", cacheKey);
          resolve(result);
        }
      } else {
        resolve(result);
      }
    } catch (error) {
      reject(error);
    }

    // Throttle: wait 200ms before processing next request
    await new Promise((res) => setTimeout(res, 200));
  }

  isProcessing = false;
};

const useApi = () => {
  const isProduction = window.isProduction === true;

  const log = useCallback(
    (...args) => {
      if (!isProduction) {
        console.log(...args);
      }
    },
    [isProduction]
  );

  const getJwtToken = useCallback(() => {
    return localStorage.getItem("accessToken");
  }, []);

  const getStoredNumber = useCallback(() => {
    const storedNumber = localStorage.getItem("storedNumber");
    return storedNumber ? parseInt(storedNumber, 10) : 0;
  }, []);

  const updateStoredNumber = useCallback(() => {
    let currentNumber = getStoredNumber();
    currentNumber += Math.floor(Math.random() * 3) + 1;

    if (currentNumber > 100) {
      currentNumber = 0;
    }

    localStorage.setItem("storedNumber", currentNumber);
    return currentNumber;
  }, [getStoredNumber]);

  // ----- NEW: decide when to attempt refresh
  const shouldAttemptRefresh = useCallback((error) => {
    var status = error && error.response && error.response.status;
    var responseData = error && error.response && error.response.data;
    var msg = String(
      (responseData && responseData.message) || (responseData && responseData.error) || ""
    ).toLowerCase();
    // Try refresh on clear auth failures, or when server hints token/jwt issues.
    return (
      status === 401 ||
      (status === 403 && (msg.includes("token") || msg.includes("jwt")))
    );
  }, []);

  // ----- NEW: refresh access token using secure HTTP-only cookie
  const refreshAccessToken = useCallback(async () => {
    if (!refreshPromise) {
      refreshPromise = axios
        .post(
          `${window.baseDomain}api/auth/jwt-refresh/`,
          {},
          {
            withCredentials: true, // include the refresh cookie
          }
        )
        .then((response) => {
          const { accessToken } = response.data || {};
          if (accessToken) {
            localStorage.setItem("accessToken", accessToken);
            log("Access token refreshed.");
            return accessToken;
          }
          throw new Error("No access token in refresh response");
        })
        .catch((err) => {
          // Refresh token likely invalid/expired → clear and redirect to login
          localStorage.removeItem("accessToken");
          localStorage.setItem("loginSuccessful", "false");
          log("Refresh failed. Redirecting to login.");
          try {
            window.location.href = "/";
          } catch (_) {}
          throw err;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
    return refreshPromise;
  }, [log]);

  // GET method with improved caching and deduplication + auto-refresh + 404 negative-caching
  const get = useCallback(
    (endpoint, config = {}) => {
      const { noCache, params, notFoundTtl, fallbackOn404, ...axiosConfig } = config;
      const cacheKey = generateCacheKey(endpoint, { params });

      const now = Date.now();

      // Check in-memory cache first (with TTL override support)
      const cached = cache.get(cacheKey);
      if (cached) {
        const { data, timestamp, ttl } = cached;
        const cacheExpiryTime =
          typeof ttl === "number"
            ? ttl
            : noCache
            ? MIN_CACHE_EXPIRY
            : CACHE_EXPIRY;

        if (now - timestamp < cacheExpiryTime) {
          log("Returning in-memory cache for:", cacheKey);
          return Promise.resolve(data);
        } else {
          // Remove expired cache
          cache.delete(cacheKey);
          log("Cache expired for:", cacheKey);
        }
      }

      // Check if a request is already pending for this endpoint
      if (pendingRequests.has(cacheKey)) {
        log("Awaiting pending request for:", cacheKey);
        return pendingRequests.get(cacheKey);
      }

      // Create a new promise for the request
      const requestPromise = new Promise((resolve, reject) => {
        const requestFn = async () => {
          const makeCall = async () => {
            log(
              "Making GET request to:",
              endpoint,
              "with config:",
              axiosConfig
            );
            const token = getJwtToken();
            const response = await axios.get(endpoint, {
              ...axiosConfig,
              params,
              headers: {
                ...axiosConfig.headers,
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            });
            log(
              "Received response for GET:",
              endpoint,
              "Status:",
              response.status,
              "Data:",
              response.data
            );
            updateStoredNumber();
            return response.data;
          };

          try {
            return await retryOn429(makeCall);
          } catch (error) {
            log("Error fetching data from GET:", endpoint, "Error:", error);

            // Attempt refresh if token likely expired/invalid
            if (shouldAttemptRefresh(error)) {
              try {
                await refreshAccessToken();
                // retry once with new token
                const data = await makeCall();
                return data;
              } catch (retryErr) {
                // Refresh failed - redirect to login is already initiated in refreshAccessToken
                // Don't re-throw to avoid unhandled promise rejection
                log("Refresh failed, redirecting to login");
                // Return a promise that never resolves since we're redirecting anyway
                return new Promise(() => {});
              }
            }

            // Handle explicit 404 with NEGATIVE CACHING (do NOT throw)
            if (error && error.response && error.response.status === 404) {
              const ttl = typeof notFoundTtl === "number" ? notFoundTtl : NOT_FOUND_TTL;
              const fallback =
                typeof fallbackOn404 !== "undefined"
                  ? fallbackOn404
                  : guessFallbackFor404(endpoint);

              log(
                "404 for:",
                endpoint,
                "→ caching fallback for",
                ttl,
                "ms; returning fallback."
              );

              // Special wrapper to tell processQueue to cache with custom TTL
              return { __cacheWithTtl: { data: fallback, ttl } };
            }

            if (error.response && error.response.status === 403) {
              // 403 without token hints: no access → logout
              localStorage.removeItem("accessToken");
              localStorage.setItem("loginSuccessful", "false");
              log("403 Forbidden: Redirecting to login.");
              try {
                window.location.href = "/";
              } catch (_) {}
            }

            console.error("Error fetching data:", error);
            throw error;
          }
        };

        // Enqueue the request
        requestQueue.push({
          requestFn,
          resolve,
          reject,
          cacheKey,
        });
        processQueue(log);
      });

      // Store the pending request
      pendingRequests.set(cacheKey, requestPromise);

      // Once the request is settled, remove it from pendingRequests
      requestPromise
        .then(() => {
          pendingRequests.delete(cacheKey);
        })
        .catch(() => {
          pendingRequests.delete(cacheKey);
        });

      return requestPromise;
    },
    [
      getJwtToken,
      updateStoredNumber,
      log,
      refreshAccessToken,
      shouldAttemptRefresh,
    ]
  );

  const mutate = useCallback(
    (method, endpoint, data = null, config = {}) => {
      var skipRefresh = config.skipRefresh || false;
      return new Promise((resolve, reject) => {
        const requestFn = async () => {
          try {
            const storedNumber = updateStoredNumber();
            let modifiedData;

            if (data instanceof FormData) {
              modifiedData = data;
            } else if (Array.isArray(data)) {
              modifiedData = data;
              modifiedData.storedNumber = storedNumber;
            } else if (data) {
              modifiedData = { ...data, storedNumber };
            }

            log(
              `${method} request to:`,
              endpoint,
              "Data:",
              modifiedData,
              "Config:",
              config
            );

            // Determine the Content-Type
            var contentType = config.headers && config.headers["Content-Type"];
            if (!contentType) {
              if (modifiedData instanceof FormData) {
                // Let Axios set the Content-Type for FormData
                contentType = undefined;
              } else {
                // Default to 'application/json' for other data types
                contentType = "application/json";
              }
            }

            const makeCall = async () => {
              const token = getJwtToken();
              const response = await axios({
                method,
                url: endpoint,
                data: modifiedData,
                ...config,
                withCredentials:
                  typeof config.withCredentials === "boolean"
                    ? config.withCredentials
                    : false, // allow caller to opt-in (e.g., login)
                headers: {
                  ...config.headers,
                  ...(contentType && { "Content-Type": contentType }),
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
              });

              log(
                `Received response for ${method}:`,
                endpoint,
                "Status:",
                response.status,
                "Data:",
                response.data
              );

              // Invalidate cache for this endpoint (keep logic as-is)
              for (let key of cache.keys()) {
                if (key.startsWith(`cache_${endpoint}`)) {
                  cache.delete(key);
                  log("Cleared cache for:", key);
                }
              }
              // Tell RTKQ what changed
              invalidateFor(endpoint);

              // Optional: immediately warm RTKQ caches so UI updates faster
              prefetchSetup();
              return response.data;
            };

            try {
              return await retryOn429(makeCall);
            } catch (error) {
              log(
                `Error with ${method} request to:`,
                endpoint,
                "Error:",
                error
              );

              // Attempt refresh on auth failures (but skip for login/auth endpoints)
              if (!skipRefresh && shouldAttemptRefresh(error)) {
                try {
                  await refreshAccessToken();
                  const data = await makeCall();
                  return data;
                } catch (retryErr) {
                  // Refresh failed - redirect to login is already initiated in refreshAccessToken
                  // Don't re-throw to avoid unhandled promise rejection
                  log("Refresh failed, redirecting to login");
                  // Return a promise that never resolves since we're redirecting anyway
                  return new Promise(() => {});
                }
              }

              if (error.response && error.response.status === 403) {
                localStorage.removeItem("accessToken");
                localStorage.setItem("loginSuccessful", "false");
                log(`${method} 403 Forbidden: Redirecting to login.`);
                try {
                  window.location.href = "/";
                } catch (_) {}
              }

              console.error(`Error with ${method} request:`, error);
              throw error;
            }
          } catch (error) {
            throw error;
          }
        };

        // Enqueue the mutation request
        requestQueue.push({ requestFn, resolve, reject, cacheKey: null });
        processQueue(log);
      });
    },
    [
      getJwtToken,
      updateStoredNumber,
      log,
      refreshAccessToken,
      shouldAttemptRefresh,
    ]
  );

  const apiMethods = useMemo(
    () => ({
      get,
      post: (endpoint, data, config) => mutate("POST", endpoint, data, config),
      put: (endpoint, data, config) => mutate("PUT", endpoint, data, config),
      patch: (endpoint, data, config) =>
        mutate("PATCH", endpoint, data, config),
      delete: (endpoint, config) => mutate("DELETE", endpoint, null, config),
    }),
    [get, mutate]
  );

  return apiMethods;
};

export default useApi;
